
import * as React from 'react'
import { Autocomplete, TextField, Grid, useTheme } from '@mui/material'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import TwoPanePaper from './TwoPanePaper'
import { Value } from './taskTypes'
import TypePopperWide from './TypePopperWide'

type Tree = Record<string, string[]>

const Pane = ({ sx: sxProp, ...props }: any) => (
  <div style={{ minWidth: 0, overflowY: 'auto', maxHeight: 500, ...sxProp }} {...props} />
)

export default function TaskTypeTwoPane({
  tree,
  disabled,
  value,
  onChange,
  placeholder = 'Select type of task',
  label = 'Type',
  size = 'small',
  sx,
  error,
  isPinned,
  isHovered,
}: {
  tree: Tree
  disabled?: boolean
  value?: Value | null
  onChange: (v: Value | null) => void
  placeholder?: string
  label?: string
  size?: 'small' | 'medium'
  sx?: any
  error?: boolean
  isPinned?: boolean
  isHovered?: boolean
}) {
  const theme = useTheme()
  const parents = React.useMemo(() => Object.keys(tree), [tree])
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const [activeParent, setActiveParent] = React.useState<string | null>(value?.base ?? null)
  const displayText = value?.label ?? ''

  const filteredParents = React.useMemo(() => {
    const q = inputValue.trim().toLowerCase()
    if (!q) return parents
    return parents.filter(p => {
      const kids = tree[p] || []
      if (formatCamelCase(p).toLowerCase().includes(q)) return true
      return kids.some(c => c.toLowerCase().includes(q))
    })
  }, [inputValue, parents, tree])

  const children = activeParent ? tree[activeParent] ?? [] : []

  const selectParent = (parent: string) => {
    const kids = tree[parent] || []
    if (!kids.length) {
      onChange({ base: parent, sub: null, label: formatCamelCase(parent) })
      setOpen(false)
      return
    }
    setActiveParent(parent)
  }

  const selectChild = (parent: string, sub: string) => {
    onChange({ base: parent, sub, label: `${formatCamelCase(parent)} ${sub}` })
    setOpen(false)
  }

  const handleClear = React.useCallback(() => {
    onChange?.(null)
    setInputValue('')
    setActiveParent(null)
    setOpen(true)
  }, [onChange])

  const gridSize = open ? 12 : isHovered ? 5 : 6

  return (
    <Grid
      item
      xs={gridSize}
      sx={t => ({
        transition: t.transitions.create(['flex-basis', 'max-width'], {
          duration: 200,
          easing: t.transitions.easing.easeInOut,
        }),
      })}
    >
      <Autocomplete
        disabled={disabled}
        freeSolo
        open={open}
        onOpen={() => setOpen(true)}
        onClose={(_, reason) => {
          if ((reason as string) === 'clear') return
          setOpen(false)
        }}
        options={parents}
        filterOptions={opts => opts}
        value={displayText}
        onChange={() => {}}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          if (reason === 'clear') {
            handleClear()
            return
          }
          setInputValue(v)
        }}
        size={size}
        sx={{
          ...sx,
          '& input.Mui-disabled': {
            color: theme.palette.text.secondary,
            WebkitTextFillColor: theme.palette.text.secondary,
          },
        }}
        PaperComponent={TwoPanePaper as any}
        PopperComponent={TypePopperWide as any}
        componentsProps={{
          clearIndicator: {
            onMouseDown: (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
            },
            onClick: (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              handleClear()
            },
            title: 'Clear selection to restore list',
          },
          paper: {
            tree,
            filteredParents,
            value,
            activeParent,
            childrenItems: children,
            onSelectParent: selectParent,
            onHoverParent: setActiveParent,
            onSelectChild: selectChild,
            onClear: handleClear,
          } as any,
          popper: {
            widthStrategy: 'match-anchor',
            maxWidth: '90vw',
            zIndex: 2700,
            offset: [0, 6],
          } as any,
        }}
        ListboxProps={{ sx: { display: 'none' } }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: theme.palette.text.primary,
                WebkitTextFillColor: theme.palette.text.primary,
              },
            }}
            inputProps={{
              ...params.inputProps,
              sx: {
                color: theme.palette.text.primary,
                WebkitTextFillColor: theme.palette.text.primary,
              },
            }}
            error={error}
            placeholder={placeholder}
            value={displayText || inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={() => setOpen(true)}
          />
        )}
      />
    </Grid>
  )
}


